import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CjppuCertificateInstructions } from "@/components/shared/CjppuCertificateInstructions";
import { FileUpload } from "@/components/shared/FileUpload";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { AuthCoverLink, AuthShell } from "@/components/shared/AuthShell";
import { OnboardingStepper } from "@/components/shared/OnboardingStepper";
import { useAuth } from "@/contexts/AuthContext";
import { serverMessage } from "@/lib/apiError";
import { getSupportMailtoHref } from "@/lib/support";
import { professionCopyKey } from "@/lib/profession-copy";
import {
  getProfessionalInviteStatus,
  lookupProfessionalInvitePersonByDocument,
  uploadProfessionalInviteCertificate,
  validateProfessionalInvite,
} from "@/services/authService";
import { stepsForStrategies } from "@/pages/auth/professional-invite-flows";
import {
  identityNamesSatisfied,
  inviteIdentityChanged,
  professionalStepperEntries,
  refusalSource,
  restoreSignup,
  tailConcernSatisfied,
  type ComposedInviteProgress,
  type ComposedStep,
  type Concern,
  type IdentityNames,
  type IdentityNamesLookup,
  type ProfessionalStepDefinition,
  type SignupTailState,
} from "@/pages/auth/professional-invite-steps";
import {
  BillingSection,
  CoverageSection,
  IdentitySection,
  PasswordSection,
  PhoneSection,
  ProfileSection,
} from "@/pages/auth/professional-invite-sections";
import { useProfessionalInvitePhone } from "@/pages/auth/use-professional-invite-phone";

/**
 * ONE ACCEPTANCE WIZARD, COMPOSED FROM THE VERIFICATIONS A PROFESSION
 * REQUIRES (spec 42ec3c57, slice 3).
 *
 * It replaces accept-notary-invitation-page.tsx and
 * accept-accountant-invitation-page.tsx, which were two six- and seven-step
 * wizards asking most of the same questions in slightly different words. A
 * profession requiring BOTH their verifications had nowhere to land at all:
 * the route picked one page by the scalar strategy, so the applicant would
 * have been shown one verification's inputs and then refused by verifyAll for
 * a field they were never asked (FM-7, landing on a real person mid-signup).
 * That is why configuring a second verification is refused server-side today,
 * and removing that refusal is what this page unblocks.
 *
 * IT BRANCHES ON REQUIREMENTS, NOT ON STRATEGY NAMES, and that is the whole
 * reason one page can serve any combination. Every behaviour that looked
 * profession-specific turns out to be keyed to something the strategy asks
 * for:
 *
 *   identity.credential  -> render the credential field, send it to the
 *                           pre-acceptance check, and name the register's
 *                           refusals
 *   identity.names       -> look the cedula up in the identity registry,
 *                           allow manual correction when it cannot answer,
 *                           and send those names along
 *   document.certificate -> a certificate step, its upload, and the human
 *                           review whose verdict can decline the invitation
 *
 * A profession requiring two strategies therefore gets the UNION of those
 * behaviours with no code here deciding that, because requirements union. The
 * strategy names appear nowhere below.
 *
 * WHAT IS STILL THE PROFESSION'S OWN: the words. professionCopy resolves the
 * four forms the strings need from the invitation's copyNamespace, so a
 * traductor is called a traductor rather than whichever profession the flow
 * was first written for.
 */

const CERT_MAX_BYTES = 10 * 1024 * 1024;
const CEDULA_LOOKUP_DEBOUNCE_MS = 450;

/** Shared so "clear the names" is one value rather than four assignments. */
const EMPTY_NAMES: IdentityNames = {
  firstName: "",
  secondFirstName: "",
  firstLastName: "",
  secondLastName: "",
};

/**
 * ONE KEY FOR EVERY PROFESSION, and the old ones are deliberately not read.
 *
 * The two pages wrote `notary-invite-progress-<token>` and
 * `accountant-invite-progress-<token>` with different payload shapes. A token
 * is single-use and these rows live in sessionStorage, so the population that
 * could have one is whoever has a half-finished signup open in a tab right
 * now; they restart at the identity step, which their token still allows.
 * Reading both old shapes to spare that case would mean carrying two dead
 * formats indefinitely.
 */
function progressKey(token: string): string {
  return `professional-invite-progress-${token}`;
}

/**
 * The server's own error code, where it sent one.
 *
 * SEPARATE FROM serverMessage because they answer different questions: the
 * code decides WHICH refusal this is, so the page can name it in the
 * profession's own words, and the message is what to show when the server
 * said something more specific than any of them.
 */
function errorCodeOf(err: unknown): string | undefined {
  return (err as { response?: { data?: { errorCode?: string } } })?.response?.data
    ?.errorCode;
}

/**
 * THE COMPILER IS THE GUARD against a step with nothing on it.
 *
 * The body, the title and the description are each chosen by concern, and a
 * concern none of them handles renders a titled, numbered, EMPTY screen with a
 * Continue button — FM-1 arriving as a blank step. A chain of ternaries cannot
 * be checked for that: it falls through to its last branch, so the applicant
 * gets the password step's heading over nothing. Switching exhaustively and
 * passing the fallthrough here makes it a BUILD error instead.
 */
function noSectionFor(concern: never): never {
  throw new Error(
    `no section for the concern "${String(concern)}", so its step would render empty`,
  );
}

export function AcceptProfessionalInvitationPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { token: tokenFromPath } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const token = tokenFromPath ?? searchParams.get("token") ?? null;
  const navigate = useNavigate();
  const { getProfessionalInvitationByToken, acceptProfessionalInviteAndLogin } =
    useAuth();

  const [invitation, setInvitation] = useState<{
    email: string;
    valid: boolean;
    expiresAt: string;
    /** Every verification this profession requires, in order. */
    verificationStrategies?: readonly string[];
    verificationStrategy?: string | null;
    /*
      The department vocabulary, served WITH the invitation. This page has no
      session, so it cannot call the authenticated coverage endpoint, and the
      list must not be hardcoded — professionalCoverageService is explicit:
      "The server owns this list; never hardcode a copy."
    */
    availableDepartments?: readonly string[];
    /*
      WHICH WORD NAMES THIS PROFESSION. The page is reached by TOKEN, not by
      profession, so this is what stops the copy calling a traductor an
      escribano (spec 798af82c).
    */
    copyNamespace?: string | null;
    /** The human review's verdict, where a strategy has one. */
    manualApprovalStatus: "pending" | "approved" | "declined" | null;
  } | null>(null);

  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const [inviteHydrated, setInviteHydrated] = useState(false);
  const [step, setStep] = useState(1);

  const [cedula, setCedula] = useState("");
  const [credential, setCredential] = useState("");
  const [stepNames, setStepNames] = useState<IdentityNames>(EMPTY_NAMES);
  const [validatedNames, setValidatedNames] = useState<{
    firstName: string;
    lastName: string;
  } | null>(null);
  /*
    THE IDENTITY THE LATER STEPS ARE ATTACHED TO, as verification accepted it.
    Kept apart from the input boxes, which the applicant can edit afterwards —
    comparing against this is what tells a corrected typo from a different
    person (control C-4).
  */
  const [validatedIdentity, setValidatedIdentity] = useState<{
    cedula: string;
    credential?: string;
  } | null>(null);

  const [coverageAreas, setCoverageAreas] = useState<string[]>([]);
  // The fiscal declaration. Held locally and submitted WITH account creation
  // rather than through its own endpoint: the fiscal columns live on the user
  // row, and that row does not exist until acceptProfessionalInviteAndLogin
  // creates it — after which this page navigates away.
  const [fiscalRut, setFiscalRut] = useState("");
  const [fiscalRazonSocial, setFiscalRazonSocial] = useState("");
  const [fiscalIssuesCfe, setFiscalIssuesCfe] = useState<boolean | null>(null);
  /*
    DOES THE SERVER ALREADY HAVE THIS DECLARATION? It separates "already on the
    user row" from "the form is empty", and the two must not be submitted the
    same way. A professional's row can exist before acceptance, which is the
    ordinary case for a reviewed profession rather than an edge one.
  */
  const [fiscalAlreadyDeclared, setFiscalAlreadyDeclared] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [certificateUploaded, setCertificateUploaded] = useState(false);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const [nameLookupLoading, setNameLookupLoading] = useState(false);
  const [namesLookupReady, setNamesLookupReady] = useState(false);
  const [nameLookupFailed, setNameLookupFailed] = useState(false);
  const cedulaLookupDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cedulaLookupLatestNormRef = useRef<string>("");

  /*
    THE FLOW THIS INVITATION'S PROFESSION WALKS, composed from its configured
    verifications. Null until the invitation loads, and null for a strategy
    the client has no requirements for — composeSteps throws on one rather
    than composing a wizard that asks for nothing, which the unavailable
    screen below reports instead of crashing.
  */
  const flow = useMemo<readonly ComposedStep[] | null>(() => {
    const strategies = invitation?.verificationStrategies?.length
      ? invitation.verificationStrategies
      : invitation?.verificationStrategy
        ? [invitation.verificationStrategy]
        : null;
    if (!strategies) return null;
    try {
      return stepsForStrategies([...strategies]);
    } catch {
      return null;
    }
  }, [invitation?.verificationStrategies, invitation?.verificationStrategy]);

  /*
    WHAT THIS PROFESSION ASKS FOR, as a set. Every branch below reads these
    rather than a strategy name, which is what lets a profession requiring two
    verifications get the union of both behaviours for free.
  */
  const requirements = useMemo(
    () => new Set((flow ?? []).flatMap((entry) => [...entry.requirements])),
    [flow],
  );
  const wantsCredential = requirements.has("identity.credential");
  const wantsNames = requirements.has("identity.names");
  const wantsCertificate = requirements.has("document.certificate");

  /*
    THE FOUR FORMS THIS PAGE'S STRINGS NEED, resolved once.

    Four rather than one because the strings genuinely differ: the title
    capitalises the inclusive form ("Escribano/a"), prose lowercases it, the
    field LABEL capitalises the credential ("Matrícula") and the help text
    lowercases it. Deriving any from another means lowercasing a title that
    then reads wrong, or capitalising a noun mid-sentence.

    EVERY ONE FALLS BACK NEUTRALLY, to "profesional" — true of every
    profession — rather than to whichever one was hardcoded before.
  */
  const professionCopy = useMemo(() => {
    const exists = (key: string): boolean => i18n.exists(key);
    const ns = invitation?.copyNamespace;
    return {
      professionNoun: t(professionCopyKey(ns, "noun", exists)),
      profession: t(professionCopyKey(ns, "nounInSentence", exists)),
      credentialLabel: t(professionCopyKey(ns, "credentialLabel", exists)),
      credential: t(professionCopyKey(ns, "credentialNoun", exists)),
    };
  }, [t, i18n, invitation?.copyNamespace]);

  /*
    THE COVER HALF — the shell the ten other signed-out pages use. `note` goes
    through Trans for its <support> tag: LiveChat is not loaded on the auth
    paths, so that link is the only way out of a stuck signup.
  */
  const invitationCover = useMemo(
    () => ({
      title: t("auth.acceptInvitationCover.title"),
      body: t("auth.acceptInvitationCover.body"),
      note: (
        <Trans
          i18nKey="auth.acceptInvitationCover.note"
          components={{ support: <AuthCoverLink href={getSupportMailtoHref()} /> }}
        />
      ),
    }),
    [t],
  );

  /*
    THE REFUSALS THAT ARE ABOUT THE INVITATION, NOT THE PHONE. Both phone
    endpoints return these, and the generic "no pudimos enviar el SMS" sends
    the applicant back to retry a code the server will never send.
  */
  const mapPhoneRefusal = useCallback(
    (errorCode: string | undefined): string | null => {
      if (errorCode === "accountant_manual_review_declined") {
        return t("auth.acceptProfessionalInvitation.manualReviewDeclined");
      }
      if (errorCode === "certificate_required") {
        return t("auth.acceptProfessionalInvitation.certificateRequired");
      }
      return null;
    },
    [t],
  );

  /*
    PROGRESS IS ONE sessionStorage ROW, MERGED. Every writer patches the same
    payload rather than rewriting it, so recording the verified phone cannot
    drop the validated names. `step` only ever moves FORWARD — it is the
    furthest step reached, not the one on screen, so walking back to fix a
    typo does not throw away progress.
  */
  const persistProgress = useCallback(
    (patch: Partial<ComposedInviteProgress>): void => {
      if (!token) return;
      try {
        const raw = sessionStorage.getItem(progressKey(token));
        const current = raw ? (JSON.parse(raw) as ComposedInviteProgress) : null;
        const merged = { ...(current ?? {}), ...patch } as ComposedInviteProgress;
        if (current?.step != null && patch.step != null) {
          merged.step = Math.max(current.step, patch.step);
        }
        // Nothing to remember until verification has named someone.
        if (!merged.firstName || !merged.cedula) return;
        sessionStorage.setItem(progressKey(token), JSON.stringify(merged));
      } catch {
        // A full or blocked sessionStorage costs the restore, not the signup.
      }
    },
    [token],
  );

  const phoneState = useProfessionalInvitePhone({
    token,
    mapRefusal: mapPhoneRefusal,
    onProgress: (patch) => persistProgress(patch),
  });
  /*
    Destructured because the restore effect depends on it. Referencing
    `phoneState.hydrate` makes the linter ask for the whole object, whose
    identity changes every render; `hydrate` itself is stable.
  */
  const { hydrate: hydratePhone } = phoneState;

  /*
    TWO READS, because the generic invitation payload is profession-agnostic
    and carries no manual-approval state — that belongs to the status
    endpoint. Both are needed HERE so a declined screen is correct on first
    paint rather than one render late. The status read is allowed to fail on
    its own: an invitation that loads with unknown approval state is the
    pre-certificate case, which the hydration effect then resolves.

    ASKED FOR EVERY PROFESSION, not only a reviewed one, because the flow is
    not known until the invitation has loaded and a second round-trip to
    discover whether to make this one would cost more than making it. For a
    profession with no review it answers nulls and falses, which is exactly
    what this page then does nothing with.
  */
  useEffect(() => {
    if (!token) {
      setLoadingInvitation(false);
      setInviteHydrated(true);
      return;
    }
    setLoadingInvitation(true);
    Promise.all([
      getProfessionalInvitationByToken(token),
      getProfessionalInviteStatus(token).catch(() => null),
    ])
      .then(([inv, status]) => {
        setInvitation({
          ...inv,
          manualApprovalStatus: status?.manualApprovalStatus ?? null,
        });
      })
      // Swallowed on purpose, and it has to be caught: without this the
      // rejection is unhandled. `invitation` stays null, which is already the
      // invalid-invitation screen below.
      .catch(() => undefined)
      .finally(() => {
        setLoadingInvitation(false);
      });
  }, [token, getProfessionalInvitationByToken]);

  /* Restore a half-finished signup. */
  useEffect(() => {
    if (loadingInvitation) return;
    if (!token || !invitation?.valid || !flow) {
      setInviteHydrated(true);
      return;
    }
    if (invitation.manualApprovalStatus === "declined") {
      setInviteHydrated(true);
      return;
    }
    let cancelled = false;
    setInviteHydrated(false);
    void (async () => {
      try {
        /*
          ASKED AGAIN, and only when a certificate is in play. The initial
          load took the status alongside the invitation so a declined screen
          is right on first paint; this second read is what catches a verdict
          or an upload that landed between then and now.
        */
        const status = wantsCertificate
          ? await getProfessionalInviteStatus(token)
          : null;
        if (cancelled) return;

        const raw = sessionStorage.getItem(progressKey(token));
        const progress = raw ? (JSON.parse(raw) as ComposedInviteProgress) : null;

        /*
          ONE DECISION, MADE OUTSIDE THIS EFFECT. restoreSignup is a pure
          function of the flow, the stored payload and the server's facts —
          see its note for why this particular decision is the one worth
          lifting out: it is the only part of the wizard that can be wrong
          without anything appearing wrong.
        */
        const restored = restoreSignup({
          flow,
          progress,
          status,
          wantsCertificate,
        });

        if (restored.declined) {
          setInvitation((prev) =>
            prev ? { ...prev, manualApprovalStatus: "declined" } : prev,
          );
          setInviteHydrated(true);
          return;
        }

        setCertificateUploaded(restored.certificateUploaded);
        // The server's fact, recorded either way — it decides later whether
        // the submit may refuse an empty form or must leave it alone.
        setFiscalAlreadyDeclared(restored.fiscalAlreadyDeclared);

        if (progress) {
          setValidatedNames({
            firstName: progress.firstName,
            lastName: progress.lastName,
          });
          setCedula(progress.cedula);
          setCredential(progress.credential ?? "");
          setValidatedIdentity({
            cedula: progress.cedula,
            credential: progress.credential,
          });
          /*
            THE PHONE COMES BACK WITH ITS VERIFICATION, because the gate is
            the whole reason the phone is its own step — restoring someone to
            the far side of a gate they would have to pass again is worse than
            not restoring them. The server is still the authority: accepting
            the invitation refuses a phone it never verified.
          */
          if (progress.phoneVerified && progress.phone) {
            hydratePhone(progress.phone, true);
          }
        }

        if (restored.step != null) setStep(restored.step);
        setInviteHydrated(true);
      } catch {
        if (!cancelled) setInviteHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    token,
    invitation?.valid,
    invitation?.manualApprovalStatus,
    loadingInvitation,
    flow,
    wantsCertificate,
    hydratePhone,
  ]);

  /*
    THE IDENTITY REGISTRY LOOKUP, for a profession whose verification reads a
    person out of one. Debounced, because it fires as the cedula is typed, and
    guarded by a ref so a slow answer for an earlier cedula cannot overwrite a
    later one.
  */
  useEffect(() => {
    if (!token || !wantsNames) return;
    const cedulaNorm = cedula.replace(/\D/g, "");
    if (cedulaLookupDebounceRef.current) {
      clearTimeout(cedulaLookupDebounceRef.current);
      cedulaLookupDebounceRef.current = null;
    }
    if (cedulaNorm.length < 7 || cedulaNorm.length > 9) {
      setNameLookupLoading(false);
      setNamesLookupReady(false);
      setNameLookupFailed(false);
      setStepNames(EMPTY_NAMES);
      return;
    }

    setNamesLookupReady(false);
    setNameLookupFailed(false);
    cedulaLookupDebounceRef.current = setTimeout(() => {
      cedulaLookupDebounceRef.current = null;
      const norm = cedula.replace(/\D/g, "");
      if (norm.length < 7 || norm.length > 9) return;
      cedulaLookupLatestNormRef.current = norm;
      setNameLookupLoading(true);
      void lookupProfessionalInvitePersonByDocument(token, norm)
        .then((res) => {
          if (cedulaLookupLatestNormRef.current !== norm) return;
          setStepNames({
            firstName: res.primerNombre,
            secondFirstName: res.segundoNombre ?? "",
            firstLastName: res.primerApellido,
            secondLastName: res.segundoApellido ?? "",
          });
          setNamesLookupReady(true);
          setNameLookupFailed(false);
        })
        .catch(() => {
          if (cedulaLookupLatestNormRef.current !== norm) return;
          // The registry could not answer (down, or no such person). Allow
          // manual entry; these are the names the applicant then confirms.
          setStepNames(EMPTY_NAMES);
          setNamesLookupReady(false);
          setNameLookupFailed(true);
        })
        .finally(() => {
          if (cedulaLookupLatestNormRef.current === norm) {
            setNameLookupLoading(false);
          }
        });
    }, CEDULA_LOOKUP_DEBOUNCE_MS);

    return () => {
      if (cedulaLookupDebounceRef.current) {
        clearTimeout(cedulaLookupDebounceRef.current);
        cedulaLookupDebounceRef.current = null;
      }
    };
  }, [cedula, token, wantsNames]);

  /*
    THE LOOKUP'S STATE AS ONE VALUE, because the gate reads all three together
    and three booleans passed separately is three chances to pass a stale one.
  */
  const namesLookup: IdentityNamesLookup = {
    loading: nameLookupLoading,
    ready: namesLookupReady,
    failed: nameLookupFailed,
  };

  const tailState: SignupTailState = {
    phoneVerified: phoneState.verified,
    profileImageFile,
    fiscalRut,
    fiscalRazonSocial,
    fiscalIssuesCfe,
    fiscalAlreadyDeclared,
    password,
    confirmPassword,
  };

  /**
   * Is this step's concern SETTLED — the question it asks, answered?
   *
   * A PLAIN FUNCTION, NOT A useCallback. It closes over tailState, which is
   * rebuilt every render because it IS this render's state, so memoising
   * would hand back a new identity every time anyway — a dependency array
   * that buys nothing and then has to be argued with.
   */
  const concernSettled = (target: Concern): boolean => {
    switch (target) {
      case "identity":
        // Verification has named someone. Not "the inputs look plausible":
        // that answer is what every later step hangs off.
        return validatedNames != null;
      case "certificate":
        return certificateUploaded;
      default:
        return tailConcernSatisfied(target, tailState);
    }
  };

  /**
   * What to SHOW for a refusal — our words where we have them.
   *
   * THE PRECEDENCE IS THE FIX. This used to be `backendMessage ?? t(key)`,
   * so the server's sentence won whenever there was one, and the server's
   * sentence for a failed register lookup says "no corresponde a un ESCRIBANO
   * habilitado" to every profession that walks that path — a traductor
   * publico was told they are not a notary. The portal owns locale on this
   * route and resolves the profession's own noun through professionCopy, so a
   * code it recognises is a code it should say itself.
   *
   * The server still wins for a code this client has no copy for, which is
   * the case where it is saying something we cannot say at all.
   */
  const refusalMessage = (code: string | undefined, backendMessage: string | null): string => {
    const source = refusalSource(code, backendMessage);
    return "key" in source
      ? t(source.key, { ...professionCopy, maxMb: 10 })
      : (source.message ?? t("auth.acceptProfessionalInvitation.error"));
  };

  const handleValidate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!token) return;
    const cedulaNorm = cedula.replace(/\D/g, "");
    const credentialNorm = credential.replace(/\D/g, "");
    if (cedulaNorm.length < 7 || cedulaNorm.length > 9) {
      toast.error(t("auth.acceptProfessionalInvitation.cedulaInvalid"));
      return;
    }
    if (wantsCredential && !credentialNorm) {
      toast.error(
        t("auth.acceptProfessionalInvitation.credentialRequired", professionCopy),
      );
      return;
    }
    if (wantsNames && !identityNamesSatisfied(namesLookup, stepNames)) {
      toast.error(t("auth.acceptProfessionalInvitation.namesLookupRequired"));
      return;
    }
    setIsValidating(true);
    try {
      /*
        THE CREDENTIAL GOES AS `credential`, named for its ROLE rather than
        for any one profession's word for it. This route resolves the
        profession from the token and asks each configured strategy to verify
        what it needs, which is what lets a profession verified the same way
        as an existing one reach this page with no code here (decision
        a78bb4f).
      */
      const result = await validateProfessionalInvite({
        token,
        cedula: cedulaNorm,
        ...(wantsCredential ? { credential: credentialNorm } : {}),
        /*
          MANUAL NAMES ONLY WHEN THE REGISTRY COULD NOT ANSWER. The server's
          fallback accepts them exactly then, and never as an override of a
          name it did supply.
        */
        ...(wantsNames && nameLookupFailed
          ? {
              manualFirstName: stepNames.firstName.trim(),
              manualSecondFirstName: stepNames.secondFirstName.trim() || undefined,
              manualFirstLastName: stepNames.firstLastName.trim(),
              manualSecondLastName: stepNames.secondLastName.trim() || undefined,
            }
          : {}),
      });
      /*
        A DIFFERENT IDENTITY INVALIDATES WHAT FOLLOWED IT. The phone, the
        photo, the coverage and the billing were collected for the person
        verification named a moment ago; if that is now someone else, keeping
        them would attach one applicant's data to another's credential (FM-5).
        Re-submitting the SAME identity clears nothing.
      */
      if (
        inviteIdentityChanged(validatedIdentity, {
          cedula: cedulaNorm,
          credential: wantsCredential ? credentialNorm : undefined,
        })
      ) {
        phoneState.clear();
        setProfileImageFile(null);
        setCoverageAreas([]);
        setFiscalRut("");
        setFiscalRazonSocial("");
        setFiscalIssuesCfe(null);
        setPassword("");
        setConfirmPassword("");
      }
      setValidatedNames({ firstName: result.firstName, lastName: result.lastName });
      setValidatedIdentity({
        cedula: cedulaNorm,
        credential: wantsCredential ? credentialNorm : undefined,
      });
      persistProgress({
        firstName: result.firstName,
        lastName: result.lastName,
        cedula: cedulaNorm,
        credential: wantsCredential ? credentialNorm : undefined,
        step: step + 1,
      });
      setStep(step + 1);
      toast.success(t("auth.acceptProfessionalInvitation.validationSuccess"));
    } catch (err: unknown) {
      const code = errorCodeOf(err);
      /*
        THE SERVER'S STATED REASON, through the shared helper. serverMessage
        guards on isAxiosError and deliberately refuses ARRAYS, so a
        validation array can never reach a toast — which the hand-rolled
        `typeof data.message === "string"` both deleted pages used does not,
        and which would render as "[object Object]" to an applicant.
      */
      const backendMessage = serverMessage(err);
      toast.error(refusalMessage(code, backendMessage));
    } finally {
      setIsValidating(false);
    }
  };

  const handleUploadCertificate = async (): Promise<void> => {
    if (!token) return;
    if (
      !certificateFile ||
      certificateFile.type !== "application/pdf" ||
      certificateFile.size > CERT_MAX_BYTES
    ) {
      toast.error(t("auth.acceptProfessionalInvitation.certificateRequired"));
      return;
    }
    setUploadingCertificate(true);
    try {
      await uploadProfessionalInviteCertificate(token, certificateFile);
      setCertificateUploaded(true);
      toast.success(t("auth.acceptProfessionalInvitation.certificateUploaded"));
      setStep(step + 1);
      persistProgress({ step: step + 1 });
    } catch (err: unknown) {
      const code = errorCodeOf(err);
      /*
        THE SERVER'S STATED REASON, through the shared helper. serverMessage
        guards on isAxiosError and deliberately refuses ARRAYS, so a
        validation array can never reach a toast — which the hand-rolled
        `typeof data.message === "string"` both deleted pages used does not,
        and which would render as "[object Object]" to an applicant.
      */
      const backendMessage = serverMessage(err);
      toast.error(refusalMessage(code, backendMessage));
      if (code === "accountant_manual_review_declined") {
        setInvitation((prev) =>
          prev ? { ...prev, manualApprovalStatus: "declined" } : prev,
        );
      }
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!token || !validatedNames) return;
    if (!phoneState.verified || !phoneState.phone.trim()) {
      toast.error(t("auth.acceptProfessionalInvitation.phoneVerifyFirst"));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t("auth.acceptProfessionalInvitation.passwordMismatch"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("auth.acceptProfessionalInvitation.passwordMin"));
      return;
    }
    if (!profileImageFile) {
      toast.error(t("auth.acceptProfessionalInvitation.profileImageRequired"));
      return;
    }
    /*
      THE FOUR FISCAL REFUSALS, made HERE as well as by the billing step's
      gate. The backend accepts their absence on purpose for deploy-order
      reasons, and assertFiscalDeclarationComplete then makes the professional
      unactivatable with no surface anywhere to go back and declare.

      SKIPPED WHEN THE SERVER ALREADY HAS THE DECLARATION. Then the local form
      is legitimately empty and refusing it would block an acceptance over
      data that already exists.
    */
    if (!fiscalAlreadyDeclared) {
      if (fiscalRut.replace(/\D/g, "").length !== 12) {
        toast.error(t("auth.professionalFiscal.rutRequired"));
        return;
      }
      if (fiscalRazonSocial.trim().length < 2) {
        toast.error(t("auth.professionalFiscal.razonSocialRequired"));
        return;
      }
      if (fiscalIssuesCfe == null) {
        toast.error(t("auth.professionalFiscal.cfeRequired"));
        return;
      }
      // A "no" is a complete ANSWER but cannot continue: under spec 300908fc
      // the professional invoices the consumer directly, so someone who issues
      // no CFE cannot perform the job and activation refuses them anyway.
      if (fiscalIssuesCfe === false) {
        toast.error(t("auth.professionalFiscal.cfeRequiredToContinue"));
        return;
      }
    }
    setIsSubmitting(true);
    try {
      await acceptProfessionalInviteAndLogin(token, {
        firstName: validatedNames.firstName,
        lastName: validatedNames.lastName,
        cedula: cedula.replace(/\D/g, ""),
        ...(wantsCredential ? { credential: credential.replace(/\D/g, "") } : {}),
        password,
        profileImageFile,
        phone: phoneState.phone.trim(),
        /*
          UNDEFINED, NEVER "". The existing-row branch of acceptance writes
          every field that is `!== undefined` — a guard written precisely so an
          older client cannot un-declare someone — and an empty string walks
          straight through it. A professional whose row already carried a
          declaration would have had their RUT and razon social overwritten
          with blanks by accepting the invitation, and the only symptom is a
          failed activation weeks later.
        */
        rut: fiscalRut.replace(/\D/g, "") || undefined,
        razonSocial: fiscalRazonSocial.trim() || undefined,
        issuesCfe: fiscalIssuesCfe ?? undefined,
        coverageAreas: coverageAreas.length > 0 ? coverageAreas : undefined,
      });
      sessionStorage.removeItem(progressKey(token));
      toast.success(t("auth.acceptProfessionalInvitation.success", professionCopy));
      navigate("/requests", { replace: true });
    } catch (err: unknown) {
      const code = errorCodeOf(err);
      /*
        THE SERVER'S STATED REASON, through the shared helper. serverMessage
        guards on isAxiosError and deliberately refuses ARRAYS, so a
        validation array can never reach a toast — which the hand-rolled
        `typeof data.message === "string"` both deleted pages used does not,
        and which would render as "[object Object]" to an applicant.
      */
      const backendMessage = serverMessage(err);
      toast.error(refusalMessage(code, backendMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingInvitation || (token && invitation?.valid && !inviteHydrated)) {
    return (
      <AuthShell>
        <Card className="w-full shadow-sm">
          <CardContent className="flex flex-col items-center gap-3 p-4 sm:p-8">
            <LoadingLogo size="sm" />
            <p className="text-sm text-muted-foreground">
              {t("auth.acceptProfessionalInvitation.loading")}
            </p>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  if (inviteHydrated && invitation?.manualApprovalStatus === "declined") {
    return (
      <AuthShell>
        <Card className="w-full shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <h1 className="text-2xl font-bold text-foreground">
              {t("auth.acceptProfessionalInvitation.declinedTitle")}
            </h1>
            <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted-foreground">
              {t("auth.acceptProfessionalInvitation.declinedBody")}
            </p>
            <Button
              asChild
              variant="outline"
              className="mt-6 w-full transition-colors duration-150 sm:w-auto"
            >
              <Link to="/login">
                {t("auth.acceptProfessionalInvitation.backToLogin")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  if (!token || !invitation?.valid) {
    return (
      <AuthShell>
        <Card className="w-full shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <h1 className="text-2xl font-bold text-foreground">
              {t("auth.acceptProfessionalInvitation.title", professionCopy)}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {!token
                ? t("auth.acceptProfessionalInvitation.tokenMissing")
                : t("auth.acceptProfessionalInvitation.invalidToken")}
            </p>
            <Button asChild variant="outline" className="mt-4 w-full">
              <Link to="/login">
                {t("auth.acceptProfessionalInvitation.backToLogin")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  /*
    A REAL INVITATION WE CANNOT COMPOSE A WIZARD FOR — a verification strategy
    this client declares no requirements for. NOT a redirect and NOT a crash:
    bouncing a professional who was genuinely invited is the same defect as
    swallowing a typo, pointed the other way, and a thrown error would be a
    white screen in an unauthenticated flow.
  */
  if (!flow || flow.length === 0) {
    return (
      <AuthShell>
        <Card className="w-full shadow-sm">
          <CardContent className="flex flex-col items-center gap-2 p-6 text-center sm:p-8">
            <h1 className="text-lg font-semibold">
              {t("auth.professionalInvite.unavailableTitle")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("auth.professionalInvite.unavailableBody")}
            </p>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  const current = Math.min(Math.max(step, 1), flow.length);
  const concern = flow[current - 1].concerns[0];
  const formId = `professional-invite-step${current}-form`;

  const goToStep = (next: number): void => {
    const clamped = Math.min(Math.max(next, 1), flow.length);
    setStep(clamped);
    persistProgress({ step: clamped });
  };

  /**
   * The one question the primary button asks.
   *
   * THE IDENTITY STEP IS THE EXCEPTION, and deliberately: its button does not
   * continue, it VALIDATES. What it needs is that the call can be attempted —
   * the registry has answered, or the applicant has supplied names because it
   * could not — whereas the stepper's "complete" for the same step is that
   * verification already named someone. Two different questions about one
   * step, which is why concernSettled is not reused here.
   */
  const canContinue =
    concern === "identity"
      ? !wantsNames || identityNamesSatisfied(namesLookup, stepNames)
      : concern === "certificate"
        ? certificateFile != null
        : concernSettled(concern);

  /*
    THE STEPPER, from the SAME predicates that gate the flow. One list, two
    readers — if each computed its own answer the stepper would sooner or
    later announce a step the button refuses to leave (FM-2).

    `complete` is the conjunction of the gate and the index, which is what
    keeps the marker on the step actually on screen when the applicant walks
    BACK: OnboardingStepper derives "current" as the first incomplete entry.
  */
  const stepDefinitions: readonly ProfessionalStepDefinition<null>[] = flow.map(
    (entry, index) => ({
      step: index + 1,
      labelKey: entry.labelKey,
      gate: () => concernSettled(entry.concerns[0]),
    }),
  );
  const stepperSteps = professionalStepperEntries(
    stepDefinitions,
    (key) => t(key),
    current,
    null,
  );

  const handleAdvance = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canContinue) return;
    goToStep(current + 1);
  };

  /*
    ONE BUTTON, THREE JOBS, on the phone step. The primary action is whatever
    is actually next — send the code, check it, or move on — so the footer
    never offers a disabled Continuar next to the button you were supposed to
    press. It is a form submit, so Enter does what the button does.
  */
  const handlePhoneStepSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (canContinue) {
      goToStep(current + 1);
      return;
    }
    if (!phoneState.codeSent) {
      void phoneState.sendCode("sms");
      return;
    }
    void phoneState.verifyCode();
  };

  const phoneStepAction = canContinue
    ? {
        label: t("auth.acceptProfessionalInvitation.continue"),
        busy: false,
        busyLabel: "",
        disabled: false,
      }
    : phoneState.codeSent
      ? {
          label: t("auth.acceptProfessionalInvitation.phoneVerify"),
          busy: phoneState.verifying,
          busyLabel: t("auth.acceptProfessionalInvitation.phoneVerifying"),
          disabled: phoneState.verifying || !phoneState.otp.trim(),
        }
      : {
          label: t("auth.acceptProfessionalInvitation.phoneSendCode"),
          busy: phoneState.sending,
          busyLabel: t("auth.acceptProfessionalInvitation.phoneSendingCode"),
          disabled: phoneState.sending || phoneState.phone.trim().length < 8,
        };

  /*
    SWITCHED, NOT CHAINED, for the reason given on noSectionFor: a ternary
    chain answers SOMETHING for a concern it does not know, and that something
    is whatever its last branch says.
  */
  const stepTitle = ((): string => {
    switch (concern) {
      case "identity":
        return t("auth.acceptProfessionalInvitation.title", professionCopy);
      case "certificate":
        return t("auth.acceptProfessionalInvitation.certificateStepTitle");
      case "phone":
        return t("auth.acceptProfessionalInvitation.phoneStepTitle");
      case "profile":
        return t("auth.acceptProfessionalInvitation.profileStepTitle");
      case "coverage":
        return t("auth.acceptProfessionalInvitation.coverageStepTitle");
      case "billing":
        return t("auth.acceptProfessionalInvitation.billingStepTitle");
      case "password":
        return t("auth.acceptProfessionalInvitation.passwordStepTitle");
      default:
        return noSectionFor(concern);
    }
  })();

  const stepDescription = ((): string => {
    switch (concern) {
      case "identity":
        /*
          WHAT THE IDENTITY STEP SAYS DEPENDS ON WHAT IT ASKS FOR. With a
          credential it explains that the register is being checked; without
          one it explains that the name is loaded and not editable. A
          profession requiring both verifications asks for the credential, so
          it gets the register's wording — the stronger claim, and the true one.
        */
        return wantsCredential
          ? t(
              "auth.acceptProfessionalInvitation.validateStepDescriptionWithCredential",
              professionCopy,
            )
          : t("auth.acceptProfessionalInvitation.validateStepDescription");
      case "certificate":
        return t("auth.acceptProfessionalInvitation.certificateStepDescription");
      case "phone":
        // Once the code is out, the line below the field says where it went —
        // repeating the generic ask above it is noise.
        return phoneState.codeSent && !phoneState.verified
          ? ""
          : t("auth.acceptProfessionalInvitation.phoneStepDescription");
      case "profile":
        return t("auth.acceptProfessionalInvitation.profileStepDescription");
      case "coverage":
        return t("auth.acceptProfessionalInvitation.coverageStepDescription");
      case "billing":
        return t("auth.acceptProfessionalInvitation.billingStepDescription");
      case "password":
        return t("auth.acceptProfessionalInvitation.passwordStepDescription");
      default:
        return noSectionFor(concern);
    }
  })();

  const stepBody = ((): JSX.Element => {
    switch (concern) {
      case "identity":
        return (
          <IdentitySection
            formId={formId}
            onSubmit={handleValidate}
            /*
              FROM THE FLOW, which is the only thing that knows where each
              requirement lands. The strategy's whole set also carries
              document.certificate, asked on a step of its own, and the
              section throws on a requirement it cannot render.
            */
            requirements={flow[current - 1].requirements}
            cedula={cedula}
            onCedulaChange={setCedula}
            credential={wantsCredential ? credential : undefined}
            onCredentialChange={wantsCredential ? setCredential : undefined}
            professionCopy={wantsCredential ? professionCopy : undefined}
            names={
              wantsNames
                ? {
                    value: stepNames,
                    onChange: (part, value) =>
                      setStepNames((prev) => ({ ...prev, [part]: value })),
                    loading: namesLookup.loading,
                    /*
                      WRITABLE ONLY WHEN THE REGISTRY COULD NOT ANSWER. Typed
                      names substitute for an outage; they never override a
                      name it did supply, which would let an applicant rename
                      the person it identified.
                    */
                    manualEntry: namesLookup.failed,
                  }
                : undefined
            }
          />
        );
      case "certificate":
        /*
          NO FORM HERE. The footer button calls the upload endpoint, because
          advancing from this step is an API write rather than a navigation.
        */
        return (
          <div className="mt-6 flex flex-col gap-5">
            <CjppuCertificateInstructions variant="accountant" />
            <FileUpload
              variant="document"
              label={t("auth.acceptProfessionalInvitation.certificateFileLabel")}
              helperText={t("auth.acceptProfessionalInvitation.certificateFileHelp")}
              acceptedMimeTypes={["application/pdf"]}
              maxFileSizeBytes={CERT_MAX_BYTES}
              disabled={uploadingCertificate}
              onFileChange={setCertificateFile}
            />
          </div>
        );
      case "phone":
        return (
          <PhoneSection
            formId={formId}
            onSubmit={handlePhoneStepSubmit}
            phone={phoneState}
          />
        );
      case "profile":
        return (
          <ProfileSection
            formId={formId}
            onSubmit={handleAdvance}
            /*
              THE NAME VERIFICATION ESTABLISHED, composed into two parts — not
              the four boxes from the identity step. Those asked who the
              applicant is; this shows who they were established to be.
            */
            firstName={validatedNames?.firstName ?? ""}
            lastName={validatedNames?.lastName ?? ""}
            onProfileImageChange={setProfileImageFile}
          />
        );
      case "coverage":
        return (
          <CoverageSection
            formId={formId}
            onSubmit={handleAdvance}
            available={invitation?.availableDepartments}
            selected={coverageAreas}
            onChange={setCoverageAreas}
            idPrefix="accept-professional-coverage"
          />
        );
      case "billing":
        return (
          <BillingSection
            formId={formId}
            onSubmit={handleAdvance}
            rut={fiscalRut}
            razonSocial={fiscalRazonSocial}
            issuesCfe={fiscalIssuesCfe}
            onRutChange={setFiscalRut}
            onRazonSocialChange={setFiscalRazonSocial}
            onIssuesCfeChange={setFiscalIssuesCfe}
            disabled={isSubmitting}
          />
        );
      case "password":
        return (
          <PasswordSection
            formId={formId}
            onSubmit={handleSubmit}
            password={password}
            confirmPassword={confirmPassword}
            onPasswordChange={setPassword}
            onConfirmPasswordChange={setConfirmPassword}
            disabled={isSubmitting}
          />
        );
      default:
        return noSectionFor(concern);
    }
  })();

  const isLastStep = current === flow.length;

  return (
    <AuthShell width="xl" coverSide="left" cover={invitationCover}>
      <div className="space-y-6">
        <Card className="w-full shadow-sm">
          <CardContent className="p-6 sm:p-8">
            {/*
              Above the title because it answers "where am I" before the title
              answers "what is this".
            */}
            <OnboardingStepper steps={stepperSteps} className="mb-6" />

            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {stepTitle}
            </h1>
            {stepDescription ? (
              <p className="mt-1.5 max-w-prose text-sm leading-relaxed text-muted-foreground">
                {stepDescription}
              </p>
            ) : null}

            {stepBody}
          </CardContent>
        </Card>

        {/*
          ONE FOOTER. The primary button stays OUTSIDE the form and reaches it
          by `form=`, which keeps the action in the same place on every step
          instead of at the bottom of whichever card happens to be tallest.
        */}
        <Card className="w-full shadow-sm">
          <CardContent className="flex flex-col-reverse gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-8 sm:py-5">
            {current === 1 ? (
              <Button
                asChild
                variant="outline"
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
              >
                <Link to="/login">
                  {t("auth.acceptProfessionalInvitation.backToLogin")}
                </Link>
              </Button>
            ) : (
              // BACK IS FREE. Every step may be returned to; only a satisfied
              // step advances.
              <Button
                type="button"
                variant="outline"
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
                disabled={isSubmitting || uploadingCertificate}
                onClick={() => goToStep(current - 1)}
              >
                {t("auth.acceptProfessionalInvitation.back")}
              </Button>
            )}

            {concern === "identity" ? (
              <Button
                type="submit"
                form={formId}
                disabled={isValidating || !canContinue}
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
              >
                {isValidating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("auth.acceptProfessionalInvitation.validating")}
                  </>
                ) : (
                  t("auth.acceptProfessionalInvitation.validate")
                )}
              </Button>
            ) : concern === "certificate" ? (
              <Button
                type="button"
                disabled={uploadingCertificate || !canContinue}
                onClick={() => void handleUploadCertificate()}
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
              >
                {uploadingCertificate ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("auth.acceptProfessionalInvitation.certificateUploading")}
                  </>
                ) : (
                  t("auth.acceptProfessionalInvitation.certificateSubmit")
                )}
              </Button>
            ) : concern === "phone" ? (
              <Button
                type="submit"
                form={formId}
                disabled={phoneStepAction.disabled}
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
              >
                {phoneStepAction.busy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {phoneStepAction.busyLabel}
                  </>
                ) : (
                  phoneStepAction.label
                )}
              </Button>
            ) : isLastStep ? (
              <Button
                type="submit"
                form={formId}
                disabled={isSubmitting || !canContinue}
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("auth.acceptProfessionalInvitation.submitting")}
                  </>
                ) : (
                  t("auth.acceptProfessionalInvitation.submit")
                )}
              </Button>
            ) : (
              <Button
                type="submit"
                form={formId}
                // The SAME predicate the stepper reads for this step. One
                // expression, two readers — they cannot disagree (FM-2).
                disabled={!canContinue}
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
              >
                {t("auth.acceptProfessionalInvitation.continue")}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </AuthShell>
  );
}
